// Contract detail + edit. SLA tiers (per-priority response/resolution) and the
// escalation matrix live here (moved from the standalone /sla + /escalations).
// Plus the commercial fields (type/value/payment/dates/renewal) and a
// COVERAGE-AREA picker: the same logical tree as /sites, with checkboxes —
// check a node to cover it (creates a contract_site_coverage row that drives
// vendor/SLA routing), uncheck to remove. Per-node system-type filter.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Trash2, Save, ArrowUpCircle, Clock, MapPinned } from 'lucide-react';
import {
  ticketingApi,
  type SlaPriorityLevel, type EscalationLevel, type ContractCoverage,
  type CoverageRule, type TechnicianEligibility,
} from '@/lib/ticketing-api';
import { locationsApi, type TreeNode } from '@/lib/inventory-api';
import { PageHeader } from '@/components/ui/DataTable';
import { StatusPill, Skeleton, EmptyState } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/Toast';
import { LogicalTree } from '@/components/LogicalTree';

const PRIORITIES = ['critical', 'high', 'medium', 'low'] as const;
const ENGAGEMENT_TYPES = ['service', 'maintenance', 'support', 'license', 'consulting', 'on_call', 'retainer'];
const PRICING_TYPES = ['monthly', 'quarterly', 'yearly', 'hourly', 'per_incident', 'hybrid'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'ILS', 'AED'];
// Tree node types that are valid contract-coverage targets (org/group/driver are not).
const COVERAGE_TYPES = ['site', 'building', 'floor', 'zone', 'device'] as const;
const fmtDate = (s?: string | null) => (s ? new Date(s).toISOString().slice(0, 10) : '');

// Escalation-policy option sets (dropdowns) — the canonical L1→L4 vocabulary.
// These English strings are the STORED values (persisted on the escalation level);
// only their displayed label is translated, via escLabel() keyed off the value.
const ESC_ROLES = ['Technician', 'Supervisor', 'Operations Manager', 'Vendor / External Provider', 'Customer Executive', 'On-call Engineer'];
const ESC_TRIGGERS = ['Ticket assigned', 'SLA at 80% of target', 'SLA breached', 'Critical failure', 'Repeated breach', 'No response in 1h', 'No response in 4h'];
const ESC_NOTIFY = ['On-call technician', 'Shift supervisor', 'Operations manager', 'Vendor escalation contact', 'Customer exec sponsor', 'NOC'];

// Stable i18n sub-key for an escalation option value (slug of the English label).
// Lets us translate the visible label while keeping the stored value untouched.
const escSlug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
// Translate the display label for a stored escalation value; fall back to the raw
// value (legacy/custom entries that aren't in our canonical vocabulary).
const escLabel = (t: (k: string, o?: Record<string, unknown>) => string, group: string, v: string) =>
  v ? t(`contractDetail.esc.${group}.${escSlug(v)}`, { defaultValue: v }) : v;

export function ContractDetailPage() {
  const { t } = useTranslation('pages');
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, isError } = useQuery({ queryKey: ['contract', id], queryFn: () => ticketingApi.getContract(id) });

  // Editable fields
  const [f, setF] = useState({
    name: '', status: 'active', type: '', priority: '', description: '',
    value: '', currency: 'USD', pricingType: '',
    startDate: '', endDate: '', signedDate: '', effectiveDate: '', renewalDate: '',
    renewalNoticeDays: '', autoRenew: false, notes: '',
  });
  const [sla, setSla] = useState<SlaPriorityLevel[]>([]);
  const [esc, setEsc] = useState<EscalationLevel[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!data) return;
    setF({
      name: data.name, status: data.status, type: data.type ?? '', priority: data.priority ?? '',
      description: data.description ?? '',
      value: data.financials?.value != null ? String(data.financials.value) : '',
      currency: data.financials?.currency ?? 'USD',
      pricingType: data.pricingType ?? '',
      startDate: fmtDate(data.startDate), endDate: fmtDate(data.endDate), signedDate: fmtDate(data.signedDate),
      effectiveDate: fmtDate(data.effectiveDate), renewalDate: fmtDate(data.renewalDate),
      renewalNoticeDays: data.renewalNoticeDays != null ? String(data.renewalNoticeDays) : '',
      autoRenew: !!data.autoRenew, notes: data.notes ?? '',
    });
    setSla(data.globalSla?.priorityLevels ?? PRIORITIES.map((p) => ({ priority: p, responseTime: 0, resolutionTime: 0 })));
    setEsc(data.escalationPolicy?.levels ?? []);
  }, [data]);

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-48" /></div>;
  if (isError || !data) return <div className="tk-card"><EmptyState icon={FileText} title={t('contractDetail.notFound.title')} hint={t('contractDetail.notFound.hint')} /></div>;

  const save = async () => {
    setSaving(true);
    try {
      const num = (s: string) => (s.trim() === '' ? null : Number(s));
      await ticketingApi.updateContract(data.id, {
        name: f.name, status: f.status,
        type: f.type || null, priority: f.priority || null, description: f.description || null,
        pricingType: f.pricingType || null,
        financials: { ...(data.financials ?? {}), value: num(f.value), currency: f.currency },
        startDate: f.startDate || null, endDate: f.endDate || null, signedDate: f.signedDate || null,
        effectiveDate: f.effectiveDate || null, renewalDate: f.renewalDate || null,
        renewalNoticeDays: num(f.renewalNoticeDays), autoRenew: f.autoRenew, notes: f.notes || null,
        globalSla: { priorityLevels: sla },
        escalationPolicy: { levels: esc },
      }, data.updatedAt);   // optimistic lock (WF-01)
      qc.invalidateQueries({ queryKey: ['contract', id] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
      setSavedAt(Date.now());
    } catch (e) {
      // 409 = someone else edited this contract since we loaded it (WF-01).
      if ((e as { response?: { status?: number } })?.response?.status === 409) {
        toast.error(t('contractDetail.staleWrite'));
        qc.invalidateQueries({ queryKey: ['contract', id] }); // pull the fresh version
      } else {
        toast.error(t('contractDetail.saveFailed'));
      }
    } finally { setSaving(false); }
  };

  const setSlaRow = (i: number, k: keyof SlaPriorityLevel, v: string) =>
    setSla((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: k === 'priority' ? v : Number(v) || 0 } : r)));
  const addEsc = () => setEsc((e) => [...e, { level: e.length + 1, role: '', triggerAfter: '', notify: '' }]);
  const setEscRow = (i: number, k: keyof EscalationLevel, v: string) =>
    setEsc((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: k === 'level' ? Number(v) || 0 : v } : r)));

  const inp = 'tk-input w-full py-1 text-sm';
  return (
    <div className="space-y-6">
      <PageHeader
        title={`${data.contractNumber} · ${data.name}`}
        subtitle={t('contractDetail.subtitle')}
        action={
          <span className="flex items-center gap-2">
            {savedAt && <span className="text-xs text-ok">{t('contractDetail.saved')}</span>}
            <Link to="/contracts" className="tk-btn-ghost">{t('contractDetail.backToContracts')}</Link>
            <button className="tk-btn-primary" disabled={saving} onClick={save}><Save size={16} /> {saving ? t('contractDetail.saving') : t('contractDetail.save')}</button>
          </span>
        }
      />

      {/* Core + commercials */}
      <div className="tk-card grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="text-xs text-muted">{t('contractDetail.fields.name')}<input className={inp} value={f.name} onChange={(e) => set('name', e.target.value)} /></label>
        <label className="text-xs text-muted">{t('contractDetail.fields.status')}
          <select className={inp} value={f.status} onChange={(e) => set('status', e.target.value)}>
            {['draft', 'active', 'pending', 'pending_renewal', 'expired', 'terminated'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="flex items-end"><StatusPill status={f.status} /></div>

        <label className="text-xs text-muted">{t('contractDetail.fields.engagementType')}
          <select className={inp} value={f.type} onChange={(e) => set('type', e.target.value)}>
            <option value="">—</option>
            {ENGAGEMENT_TYPES.map((et) => <option key={et} value={et}>{t(`contractDetail.esc.engagementTypes.${et}`, { defaultValue: et.replace('_', '-') })}</option>)}
          </select>
        </label>
        <label className="text-xs text-muted">{t('contractDetail.fields.priority')}
          <select className={inp} value={f.priority} onChange={(e) => set('priority', e.target.value)}>
            <option value="">—</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="text-xs text-muted">{t('contractDetail.fields.paymentCadence')}
          <select className={inp} value={f.pricingType} onChange={(e) => set('pricingType', e.target.value)}>
            <option value="">—</option>
            {PRICING_TYPES.map((p) => <option key={p} value={p}>{p.replace('_', ' ')}</option>)}
          </select>
        </label>

        <label className="text-xs text-muted">{t('contractDetail.fields.value')}
          <input className={inp} type="number" min={0} value={f.value} onChange={(e) => set('value', e.target.value)} placeholder="0" />
        </label>
        <label className="text-xs text-muted">{t('contractDetail.fields.currency')}
          <select className={inp} value={f.currency} onChange={(e) => set('currency', e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <span />

        <label className="col-span-1 text-xs text-muted md:col-span-3">{t('contractDetail.fields.description')}
          <textarea className={inp} rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} />
        </label>
      </div>

      {/* Dates + renewal */}
      <div className="tk-card grid grid-cols-2 gap-3 md:grid-cols-4">
        <label className="text-xs text-muted">{t('contractDetail.fields.start')}<input className={inp} type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} /></label>
        <label className="text-xs text-muted">{t('contractDetail.fields.end')}<input className={inp} type="date" value={f.endDate} onChange={(e) => set('endDate', e.target.value)} /></label>
        <label className="text-xs text-muted">{t('contractDetail.fields.signed')}<input className={inp} type="date" value={f.signedDate} onChange={(e) => set('signedDate', e.target.value)} /></label>
        <label className="text-xs text-muted">{t('contractDetail.fields.effective')}<input className={inp} type="date" value={f.effectiveDate} onChange={(e) => set('effectiveDate', e.target.value)} /></label>
        <label className="text-xs text-muted">{t('contractDetail.fields.renewalDate')}<input className={inp} type="date" value={f.renewalDate} onChange={(e) => set('renewalDate', e.target.value)} /></label>
        <label className="text-xs text-muted">{t('contractDetail.fields.renewalNotice')}<input className={inp} type="number" min={0} value={f.renewalNoticeDays} onChange={(e) => set('renewalNoticeDays', e.target.value)} /></label>
        <label className="flex items-end gap-2 text-xs text-muted">
          <input type="checkbox" className="h-4 w-4" checked={f.autoRenew} onChange={(e) => set('autoRenew', e.target.checked)} /> {t('contractDetail.fields.autoRenew')}
        </label>
      </div>

      {/* SLA per priority */}
      <div className="tk-card">
        <div className="mb-1 flex items-center gap-2 text-sm font-medium"><Clock size={15} className="text-primary" /> {t('contractDetail.sla.heading')}</div>
        <p className="mb-3 text-xs text-muted">{t('contractDetail.sla.description')}</p>
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 border-b border-white/10 pb-1 text-xs text-muted"><span>{t('contractDetail.sla.columns.priority')}</span><span>{t('contractDetail.sla.columns.response')}</span><span>{t('contractDetail.sla.columns.resolution')}</span></div>
        {sla.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr] items-center gap-2 border-b border-white/5 py-1.5 last:border-0">
            <span className="text-sm font-medium capitalize">{r.priority}</span>
            <input className={inp} type="number" min={0} value={r.responseTime} onChange={(e) => setSlaRow(i, 'responseTime', e.target.value)} />
            <input className={inp} type="number" min={0} value={r.resolutionTime} onChange={(e) => setSlaRow(i, 'resolutionTime', e.target.value)} />
          </div>
        ))}
      </div>

      {/* Escalation policy */}
      <div className="tk-card">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium"><ArrowUpCircle size={15} className="text-primary" /> {t('contractDetail.escalation.heading')}</div>
          <button className="tk-btn-ghost px-2 py-1 text-xs" onClick={addEsc}><Plus size={14} /> {t('contractDetail.escalation.addLevel')}</button>
        </div>
        {esc.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted">{t('contractDetail.escalation.empty')}</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-[40px_1fr_1.2fr_1fr_32px] gap-2 text-xs text-muted"><span>{t('contractDetail.escalation.columns.level')}</span><span>{t('contractDetail.escalation.columns.role')}</span><span>{t('contractDetail.escalation.columns.triggerAfter')}</span><span>{t('contractDetail.escalation.columns.notify')}</span><span /></div>
            {esc.map((r, i) => (
              <div key={i} className="grid grid-cols-[40px_1fr_1.2fr_1fr_32px] items-center gap-2">
                <input className={inp} type="number" min={1} value={r.level} onChange={(e) => setEscRow(i, 'level', e.target.value)} />
                <EscSelect value={r.role} options={ESC_ROLES} labelGroup="roles" placeholder={t('contractDetail.escalation.rolePlaceholder')} onChange={(v) => setEscRow(i, 'role', v)} />
                <EscSelect value={r.triggerAfter} options={ESC_TRIGGERS} labelGroup="triggers" placeholder={t('contractDetail.escalation.triggerPlaceholder')} onChange={(v) => setEscRow(i, 'triggerAfter', v)} />
                <EscSelect value={r.notify} options={ESC_NOTIFY} labelGroup="notify" placeholder={t('contractDetail.escalation.notifyPlaceholder')} onChange={(v) => setEscRow(i, 'notify', v)} />
                <button className="tk-btn-ghost px-1.5 py-1" title={t('contractDetail.escalation.remove')} onClick={() => setEsc((e) => e.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coverage area — the logical tree (same as /sites) with check/uncheck */}
      <CoverageSection contractId={data.id} />

      {/* Phase-1: normalized coverage rules (include/exclude by dimension) */}
      <CoverageRulesSection contractId={data.id} />

      {/* Phase-1: technicians the customer approves for this contract */}
      <ApprovedTechniciansSection contractId={data.id} vendorId={data.vendorId ?? null} />

      {/* Notes */}
      <div className="tk-card">
        <div className="mb-2 text-sm font-medium">{t('contractDetail.notes.heading')}</div>
        <textarea className={inp} rows={3} value={f.notes} onChange={(e) => set('notes', e.target.value)} placeholder={t('contractDetail.notes.placeholder')} />
      </div>
    </div>
  );
}

// A dropdown for an escalation field. Shows the canonical options; if the saved
// value isn't in the list (legacy/custom), it's added so nothing is lost.
function EscSelect({ value, options, labelGroup, placeholder, onChange }: {
  value: string; options: string[]; labelGroup: string; placeholder: string; onChange: (v: string) => void;
}) {
  const { t } = useTranslation('pages');
  const opts = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <select className="tk-input w-full py-1 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {opts.map((o) => <option key={o} value={o}>{escLabel(t, labelGroup, o)}</option>)}
    </select>
  );
}

// Coverage-area picker: the /sites logical tree with checkboxes. Checking a node
// creates a coverage row immediately; unchecking deletes it. Each covered node
// can be scoped to a system type.
function CoverageSection({ contractId }: { contractId: string }) {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const { data: tree } = useQuery({ queryKey: ['locations', 'tree'], queryFn: locationsApi.tree, retry: false });
  const { data: systemTypes } = useQuery({ queryKey: ['system-types'], queryFn: ticketingApi.listSystemTypes });
  const { data: coverage } = useQuery({ queryKey: ['contract-coverage', contractId], queryFn: () => ticketingApi.contractCoverage(contractId) });
  const [busy, setBusy] = useState(false);

  // Map nodeId → the coverage row covering it (for check state + delete + system-type).
  const byNode = useMemo(() => {
    const m = new Map<string, ContractCoverage>();
    (coverage ?? []).forEach((c) => m.set(c.nodeId, c));
    return m;
  }, [coverage]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['contract-coverage', contractId] });

  const onCheck = async (node: TreeNode, checked: boolean) => {
    setBusy(true);
    try {
      if (checked) {
        await ticketingApi.addCoverage(contractId, { nodeType: node.type, nodeId: node.id, includeSubSystems: true });
      } else {
        const row = byNode.get(node.id);
        if (row) await ticketingApi.removeCoverage(contractId, row.id);
      }
      refresh();
    } finally { setBusy(false); }
  };

  const setNodeSystemType = async (node: TreeNode, systemTypeId: string) => {
    const row = byNode.get(node.id);
    if (!row) return;
    setBusy(true);
    try {
      // Re-create with the chosen system type (simple + reliable vs PATCH).
      await ticketingApi.removeCoverage(contractId, row.id);
      await ticketingApi.addCoverage(contractId, { nodeType: node.type, nodeId: node.id, systemTypeId: systemTypeId || null, includeSubSystems: true });
      refresh();
    } finally { setBusy(false); }
  };

  return (
    <div className="tk-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-medium"><MapPinned size={15} className="text-primary" /> {t('contractDetail.coverage.heading')}</div>
      <p className="mb-3 text-xs text-muted">{t('contractDetail.coverage.description')}</p>
      {!tree ? <Skeleton className="h-40" /> : (
        <div className={busy ? 'pointer-events-none opacity-60' : ''}>
          <LogicalTree
            roots={tree.logical}
            showHealth={false}
            checkable
            checkableTypes={[...COVERAGE_TYPES]}
            isChecked={(n) => byNode.has(n.id)}
            onCheck={onCheck}
            renderNodeExtra={(n) => (
              <select
                className="tk-input ml-auto w-36 py-0.5 text-xs"
                value={byNode.get(n.id)?.systemTypeId ?? ''}
                onChange={(e) => setNodeSystemType(n, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                title={t('contractDetail.coverage.systemTypeTooltip')}
              >
                <option value="">{t('contractDetail.coverage.allSystems')}</option>
                {(systemTypes ?? []).map((s) => <option key={s.id} value={s.code}>{s.name}</option>)}
              </select>
            )}
          />
        </div>
      )}
    </div>
  );
}

// ── Phase 1: normalized coverage rules ──────────────────────────────────────
// INCLUDE/EXCLUDE rules by dimension (site/device/category/priority/…). These
// layer on top of the tree coverage above; the resolver evaluates both, and an
// EXCLUDE beats an INCLUDE at equal-or-higher priority.
const RULE_TYPES = ['CUSTOMER', 'SITE', 'BUILDING', 'FLOOR', 'REGION', 'CATEGORY', 'SYSTEM', 'DEVICE', 'DEVICE_TYPE', 'MANUFACTURER', 'MODEL', 'TAG', 'WARRANTY_STATUS', 'PROJECT', 'FAULT_TYPE', 'PRIORITY'];
const ID_RULE_TYPES = new Set(['SITE', 'BUILDING', 'FLOOR', 'ZONE', 'DEVICE', 'PROJECT']);

function CoverageRulesSection({ contractId }: { contractId: string }) {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const toast = useToast();
  const { data: rules } = useQuery({ queryKey: ['coverage-rules', contractId], queryFn: () => ticketingApi.listCoverageRules(contractId) });
  const [ruleType, setRuleType] = useState('SITE');
  const [operator, setOperator] = useState('INCLUDE');
  const [target, setTarget] = useState('');
  const [priority, setPriority] = useState(100);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['coverage-rules', contractId] });

  const add = async () => {
    setBusy(true);
    try {
      const isId = ID_RULE_TYPES.has(ruleType);
      await ticketingApi.addCoverageRule(contractId, {
        ruleType, operator,
        targetId: isId ? (target || null) : null,
        targetValue: isId ? null : (target || null),
        priority,
      });
      setTarget('');
      refresh();
    } catch { toast.error(t('contractDetail.coverageRules.addFailed', { defaultValue: 'Could not add rule' })); }
    finally { setBusy(false); }
  };
  const remove = async (id: string) => { await ticketingApi.removeCoverageRule(contractId, id); refresh(); };

  const inp = 'tk-input py-1 text-sm';
  return (
    <div className="tk-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-medium">
        <MapPinned size={15} className="text-primary" /> {t('contractDetail.coverageRules.heading', { defaultValue: 'Coverage rules' })}
      </div>
      <p className="mb-3 text-xs text-muted">{t('contractDetail.coverageRules.description', { defaultValue: 'Include or exclude what this contract covers, by dimension. Exclusions win at equal priority.' })}</p>

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <select className={inp} value={operator} onChange={(e) => setOperator(e.target.value)}>
          <option value="INCLUDE">{t('contractDetail.coverageRules.include', { defaultValue: 'Include' })}</option>
          <option value="EXCLUDE">{t('contractDetail.coverageRules.exclude', { defaultValue: 'Exclude' })}</option>
        </select>
        <select className={inp} value={ruleType} onChange={(e) => setRuleType(e.target.value)}>
          {RULE_TYPES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ').toLowerCase()}</option>)}
        </select>
        {ruleType !== 'CUSTOMER' && (
          <input
            className={`${inp} w-56`} value={target} onChange={(e) => setTarget(e.target.value)}
            placeholder={ID_RULE_TYPES.has(ruleType)
              ? t('contractDetail.coverageRules.targetIdPlaceholder', { defaultValue: 'target id (uuid)' })
              : t('contractDetail.coverageRules.targetValuePlaceholder', { defaultValue: 'value (e.g. high, offline)' })}
          />
        )}
        <input className={`${inp} w-20`} type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} title="priority" />
        <button className="tk-btn tk-btn-primary py-1 text-sm" disabled={busy} onClick={add}><Plus size={14} /> {t('common.add', { defaultValue: 'Add', ns: 'common' })}</button>
      </div>

      {!rules?.length ? (
        <p className="text-xs text-muted">{t('contractDetail.coverageRules.empty', { defaultValue: 'No explicit rules — routes via tree coverage above.' })}</p>
      ) : (
        <div className="space-y-1">
          {rules.map((r: CoverageRule) => (
            <div key={r.id} className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm">
              <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${r.operator === 'EXCLUDE' ? 'bg-red-500/15 text-red-500' : 'bg-emerald-500/15 text-emerald-500'}`}>{r.operator}</span>
              <span className="font-medium">{r.ruleType.replace(/_/g, ' ').toLowerCase()}</span>
              <span className="text-muted">{r.targetValue ?? r.targetId ?? '—'}</span>
              <span className="ml-auto text-xs text-muted">p{r.priority}</span>
              <button className="text-muted hover:text-red-500" onClick={() => remove(r.id)} title={t('common.remove', { defaultValue: 'Remove', ns: 'common' })}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Phase 1: approved technicians ───────────────────────────────────────────
// The customer approves/revokes which of the vendor's technicians may serve this
// contract. Only approved (or otherwise eligible) technicians can be assigned.
function ApprovedTechniciansSection({ contractId, vendorId }: { contractId: string; vendorId: string | null }) {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const { data: eligible } = useQuery({ queryKey: ['eligible-techs', contractId], queryFn: () => ticketingApi.listEligibleTechnicians(contractId) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['eligible-techs', contractId] });

  const toggle = async (row: TechnicianEligibility) => {
    if (row.approvedByCustomer) await ticketingApi.revokeTechnician(contractId, row.technicianId);
    else await ticketingApi.approveTechnician(contractId, row.technicianId);
    refresh();
  };

  return (
    <div className="tk-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-medium">
        <Clock size={15} className="text-primary" /> {t('contractDetail.approvedTechs.heading', { defaultValue: 'Approved technicians' })}
      </div>
      <p className="mb-3 text-xs text-muted">{t('contractDetail.approvedTechs.description', { defaultValue: "The vendor's technicians the customer permits to serve this contract." })}</p>
      {!vendorId ? (
        <p className="text-xs text-muted">{t('contractDetail.approvedTechs.noVendor', { defaultValue: 'Assign a vendor to the contract first.' })}</p>
      ) : !eligible?.length ? (
        <p className="text-xs text-muted">{t('contractDetail.approvedTechs.empty', { defaultValue: 'No technicians in the eligibility pool yet.' })}</p>
      ) : (
        <div className="space-y-1">
          {eligible.map((row: TechnicianEligibility) => (
            <div key={row.id} className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm">
              <span className="text-sm">{row.technicianName || row.technicianId.slice(0, 8)}</span>
              <span className="text-xs text-muted">{row.eligibilityType.toLowerCase()}</span>
              <span className={`ml-auto rounded px-1.5 py-0.5 text-xs font-medium ${row.approvedByCustomer ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-500'}`}>
                {row.approvedByCustomer ? t('contractDetail.approvedTechs.approved', { defaultValue: 'approved' }) : t('contractDetail.approvedTechs.pending', { defaultValue: 'pending' })}
              </span>
              <button className="tk-btn py-0.5 text-xs" onClick={() => toggle(row)}>
                {row.approvedByCustomer ? t('contractDetail.approvedTechs.revoke', { defaultValue: 'Revoke' }) : t('contractDetail.approvedTechs.approve', { defaultValue: 'Approve' })}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
